import React, { useEffect, useRef, useState } from "react";

import { shouldShowSentilink } from "shared/components/vega/ApplicantSummaryV2/sentilinkIdentityLines";
import { findLumenResult } from "shared/components/vega/LumenV2";
import { useDeviceType, usePermissions } from "shared/hooks";
import {
  isWorkflowStarted,
  useGetApplicantFromApplicationService,
  useGetApplicantWorkflowRun,
} from "shared/hooks/api";
import { isVeroSubmittedApplication } from "shared/utils/deal-status";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import { CardGridSkeleton } from "../ApplicantSummarySkeleton";
import { CategoryId } from "../constants";
import { useDealSummaryRedesign } from "../../useDealSummaryRedesign";

import ApplicantGroupCard from "./components/ApplicantGroupCard";
import ApplicantInfoCardWithActions from "./components/ApplicantInfoCard";
import { isLumenDataAvailable } from "./helpers/applicantHistoryHelpers";

import {
  getApplicationStatus,
  getCardDetailsByCategory,
} from "./helpers/applicantStatusHelpers";

import "./styles.css";

const ApplicantSummary = ({
  id,
  lumenRun,
  legacyApp,
  shouldFetchMissingLegacyApp = true,
  fullApplication, // Full application data passed from parent (already fetched)
  isLumenStillCalculating,
  refetchDeal,
  dealId,
  dealStatus,
  cosignApplication = null,
  autoOpenIdVerificationModal = false,
  vero1Neutral = false,
  vero1Verdict,
  vero1CardOutcomes,
  legacySummaryPending = false,
}) => {
  const { applicant } = useGetApplicantFromApplicationService(id);
  const { applicantWorkflow, isLoading: isApplicantWorkflowLoading } =
    useGetApplicantWorkflowRun(id);
  const applicantWorkflowLookupFailed =
    Boolean(applicantWorkflow?.error) && isWorkflowStarted(applicantWorkflow);
  const applicantLumenResult = findLumenResult(lumenRun, applicant?.id);
  const applicationLumenResult = findLumenResult(lumenRun);
  // Check if user has permission to view background check details
  const { canAccessSensitiveDealDataInLumen } = usePermissions();
  const { isMobile } = useDeviceType();
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);
  const useDealSummaryRedesignProduct1270 = useDealSummaryRedesign();
  const pmsApprovedNoVeroEnabled = useVeroFlag(
    VeroFlag.useYardiApprovedNoVeroStatus
  );
  const enableMonthlyIncomeDisplay = useVeroFlag(
    VeroFlag.enableMonthlyIncomeDisplayProduct1486
  );
  const useAppSummaryTabRestructure = useVeroFlag(
    VeroFlag.useAppSummaryTabRestructure
  );

  // Track if user has manually toggled the expand/collapse state
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const didAutoExpandForIdFraudReview = useRef(false);

  // Collapse state - default to collapsed when feature flag is enabled
  const [isExpanded, setIsExpanded] = useState(
    !useDealsListUiRedesign || autoOpenIdVerificationModal
  );
  // Local one-shot for the ID card. Parent may keep autoOpenIdVerificationModal
  // true; we must not pass that through after the first expanded paint or
  // collapse/re-expand remounts the card and reopens the modal.
  const [autoOpenIdCard, setAutoOpenIdCard] = useState(
    autoOpenIdVerificationModal
  );

  // Only sync with feature flag changes if user hasn't manually toggled.
  // Auto-open from Review Identity Risk Check must be one-shot: if we keep
  // forcing expanded while autoOpenIdVerificationModal stays true, collapsing
  // the header (which sets hasUserInteracted) immediately snaps it back open.
  useEffect(() => {
    if (autoOpenIdVerificationModal && !didAutoExpandForIdFraudReview.current) {
      didAutoExpandForIdFraudReview.current = true;
      setIsExpanded(true);
      setHasUserInteracted(true);
      setAutoOpenIdCard(true);
      return;
    }
    if (!hasUserInteracted) {
      setIsExpanded(!useDealsListUiRedesign);
    }
  }, [autoOpenIdVerificationModal, useDealsListUiRedesign, hasUserInteracted]);

  useEffect(() => {
    if (!isExpanded || !autoOpenIdCard) {
      return;
    }
    setAutoOpenIdCard(false);
  }, [autoOpenIdCard, isExpanded]);

  // Handle toggle with user interaction tracking
  const handleToggle = () => {
    if (useDealsListUiRedesign) {
      setHasUserInteracted(true);
      setIsExpanded(!isExpanded);
    }
  };

  // Use full application data passed from parent (no redundant API call)
  const application = fullApplication;

  if (applicant?.error) {
    return null;
  }

  const isLumenAvailable = isLumenDataAvailable(
    applicantLumenResult,
    isLumenStillCalculating
  );

  // Drive the Income "Pending" vs "could not be verified" copy off a real VERO
  // submission (submittedAt), not the PMS-inclusive `isSubmitted`, so the cards
  // stay consistent with the applicant pills and the header banner for deals
  // approved directly in Yardi. Resolve `isSubmitted`/`submittedAt` through the
  // same `legacyApp.info -> applicant` fallback chain that the sibling
  // `ApplicantInfoCard` pills use, so the cards and pills can't disagree when the
  // legacy `info` block is absent and only the application-service `applicant`
  // carries the submission signal.
  //
  // Gated behind the same flag as the banner/list/pills so the whole screen
  // flips together: when the flag is off, fall back to plain `isSubmitted`
  // (pre-PRODUCT-1522 behavior).
  const rawIsSubmitted = legacyApp?.info?.isSubmitted ?? applicant?.isSubmitted;
  const isSubmitted = pmsApprovedNoVeroEnabled
    ? isVeroSubmittedApplication({
        isSubmitted: rawIsSubmitted,
        submittedAt: legacyApp?.info?.submittedAt ?? applicant?.submittedAt,
      })
    : rawIsSubmitted;
  const applicantName =
    legacyApp?.info?.name ||
    [applicant?.firstName, applicant?.lastName].filter(Boolean).join(" ") ||
    [legacyApp?.applicant?.firstName, legacyApp?.applicant?.lastName]
      .filter(Boolean)
      .join(" ") ||
    undefined;
  const applicantRole = applicant?.party || legacyApp?.applicant?.type;
  const showBackgroundCreditSection =
    applicant?.shouldRunBackgroundAndCreditChecks;
  const showIdentitySection = legacyApp?.identity?.enabled;

  // Use the helper function to generate all card details
  const cardDetailsByCategory = getCardDetailsByCategory(
    lumenRun,
    legacyApp,
    application,
    applicantLumenResult,
    applicationLumenResult,
    isLumenAvailable,
    isSubmitted,
    showBackgroundCreditSection,
    canAccessSensitiveDealDataInLumen,
    useAppSummaryTabRestructure,
    enableMonthlyIncomeDisplay,
    shouldShowSentilink(
      legacyApp?.sentilink,
      applicantWorkflow,
      isApplicantWorkflowLoading,
      applicantWorkflowLookupFailed
    )
  );

  return (
    <div
      className={`app-summary-v2 applicant-summary-container${
        useDealSummaryRedesignProduct1270 ? " lg-secondary" : ""
      }`}
    >
      {/* Grouped Container for Header and Cards */}
      <div
        className={`applicant-detail-group ${
          useDealsListUiRedesign && !isMobile ? "aligned" : ""
        }`}
      >
        {/* Banking Style Header - Applicant Identity */}
        <div
          className="applicant-header-wrapper"
          style={{
            width: "100%",
            cursor: useDealsListUiRedesign ? "pointer" : "default",
          }}
          onClick={handleToggle}
        >
          <ApplicantInfoCardWithActions
            id={id}
            applicant={applicant}
            legacyApp={legacyApp}
            legacySummaryPending={legacySummaryPending}
            shouldFetchMissingLegacyApp={shouldFetchMissingLegacyApp}
            refetchDeal={refetchDeal}
            dealId={dealId}
            applicationStatus={getApplicationStatus(fullApplication, legacyApp)}
          />
        </div>

        {/* Verification Cards Grid - Collapsible */}
        {isExpanded && legacySummaryPending && (
          <div className="verification-cards-section">
            {/* identity's existence and income's content come from the legacy
                summary — hold slots until it lands so the grid settles once */}
            <CardGridSkeleton
              slots={
                // four reads as a full row; only a known false narrows to two
                applicant?.shouldRunBackgroundAndCreditChecks === false ? 2 : 4
              }
            />
          </div>
        )}
        {isExpanded && !legacySummaryPending && (
          <div className="verification-cards-section">
            <div className="attributes-grid">
              {Object.entries(cardDetailsByCategory).map(
                ([categoryId, details]) => {
                  // Convert string to CategoryId enum value
                  const category = categoryId as CategoryId;

                  // Skip background check sections if not showing
                  if (
                    [
                      CategoryId.CREDIT_HISTORY,
                      CategoryId.RENTAL_HISTORY,
                      CategoryId.CRIMINAL_HISTORY,
                    ].includes(category) &&
                    !showBackgroundCreditSection
                  ) {
                    return null;
                  }

                  // Skip identity section if not enabled
                  if (
                    category === CategoryId.ID_VERIFICATION &&
                    !showIdentitySection
                  ) {
                    return null;
                  }

                  return (
                    <div key={categoryId} className="attribute-card-container">
                      <ApplicantGroupCard
                        {...details}
                        cosignApproved={
                          cosignApplication?.status === "approved"
                        }
                        cosignUuid={cosignApplication?.cosignUuid}
                        applicantName={applicantName}
                        applicantRole={applicantRole}
                        applicationStatus={
                          fullApplication?.applicationSummary?.status ??
                          fullApplication?.status ??
                          legacyApp?.status ??
                          legacyApp?.info?.status
                        }
                        dealStatus={dealStatus}
                        neutral={vero1Neutral}
                        vero1Verdict={vero1Verdict}
                        dealId={dealId}
                        vero1ReviewCategory={
                          vero1CardOutcomes?.[category]?.reviewCategory
                        }
                        vero1Edited={
                          (vero1CardOutcomes?.[category]?.editedCount ?? 0) > 0
                        }
                        vero1CardTone={vero1CardOutcomes?.[category]?.tone}
                        autoOpenModal={
                          autoOpenIdCard &&
                          category === CategoryId.ID_VERIFICATION
                        }
                      />
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicantSummary;
